import PropTypes from 'prop-types'
import { Box, ButtonBase, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import { useSmartTranslation } from '../../../generic/hooks/useSmartTranslation'
import { EYEBROW_SX, codeSx } from '../../../generic/qr/qrReadingStyles'
import { isOpenable } from './qrDocumentTree'

/**
 * Picks which element of a scanned order's structure to read: the order, one of its lines, or one of
 * the fabrication orders of a line.
 *
 * Deliberately NOT a tree widget. Everything arrives expanded and nothing here is ever collapsed — the
 * point of scanning an order is reaching a document two levels down, and an operator wearing gloves
 * should not have to open two nodes to find out whether anything is there. What was left of the tree
 * was its furniture: expand and collapse boxes that did nothing, and an "end" box on every leaf that
 * reads as a cross on the very nodes that hold the drawings.
 *
 * So each element is a row, and a row answers the three questions in the order they get asked: what
 * this level IS, what it is called, and whether there is anything in it. Indentation with a guide line
 * carries the nesting, the counter carries the reason to tap, and the picked row carries the same mint
 * edge the widget's card does.
 *
 * An empty node stays visible but disabled: a line with no documents of its own may still hold the
 * fabrication order that has the drawings, so hiding it would hide its children.
 */

// What the level is. Read off the resource type Sun addressed the documents by, with the document scope
// as the fallback for a level that is a view of the element rather than another element ('Instance',
// 'Reference'), and nothing invented for a type this front has not met.
const LEVEL_LABELS = {
  ORDER: 'Order',
  ORDER_LINE: 'Line',
  FABRICATION_ORDER: 'Fabrication Order'
}
const SCOPE_LABELS = { REFERENCE: 'Reference', INSTANCE: 'Instance' }

const levelLabel = (node) => LEVEL_LABELS[node.resourceType] || SCOPE_LABELS[node.scope] || ''

// The node's own code (ORD-7-1, OF-2026-0184) is what the operator reads on paper; only a level with no
// name of its own falls back to what it is.
const nodeName = (node, t_) => node.name !== '' ? node.name : t_(levelLabel(node))

const QrDocumentsTree = ({ nodes = [], selectedKey = '', onSelect }) => {
  const { t_ } = useSmartTranslation()

  return (
    <Box
      sx={{
        width: '16rem',
        flexShrink: 0,
        minHeight: 0,
        overflow: 'auto',
        pr: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5
      }}
    >
      {nodes.map((node) => (
        <QrDocumentsBranch key={node.key} node={node} selectedKey={selectedKey} onSelect={onSelect} t_={t_} />
      ))}
    </Box>
  )
}

const QrDocumentsBranch = ({ node, selectedKey, onSelect, t_ }) => (
  <>
    <QrDocumentsRow node={node} selected={node.key === selectedKey} onSelect={onSelect} t_={t_} />

    {node.children.length > 0 && (
      // One guide line per level, so how deep a row sits is seen rather than counted.
      <Box
        sx={{
          ml: 1,
          pl: 1,
          borderLeft: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5
        }}
      >
        {node.children.map((child) => (
          <QrDocumentsBranch key={child.key} node={child} selectedKey={selectedKey} onSelect={onSelect} t_={t_} />
        ))}
      </Box>
    )}
  </>
)

const QrDocumentsRow = ({ node, selected, onSelect, t_ }) => {
  const openable = isOpenable(node)

  return (
    <ButtonBase
      disabled={!openable}
      onClick={() => onSelect(node.key)}
      sx={(theme) => ({
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        width: '100%',
        minHeight: '2.75rem',
        px: 1,
        py: 0.5,
        borderRadius: '0.5rem',
        textAlign: 'left',
        justifyContent: 'flex-start',
        transition: 'background-color 0.15s ease',
        border: '1px solid ' + (selected ? alpha(theme.palette.primary.main, 0.35) : 'transparent'),
        borderLeft: '3px solid ' + (selected ? theme.palette.primary.main : 'transparent'),
        backgroundColor: selected ? alpha(theme.palette.primary.main, 0.09) : 'transparent',
        '&:hover': {
          backgroundColor: alpha(theme.palette.primary.main, selected ? 0.14 : 0.06)
        },
        // An empty level is dimmed one piece at a time below, not by fading the whole row: it still has
        // to be readable, because what it says is that a line of the order exists and holds nothing.
        '&.Mui-disabled': { opacity: 1 }
      })}
    >
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography sx={EYEBROW_SX} noWrap>{t_(levelLabel(node))}</Typography>
        <Typography
          noWrap
          sx={{ ...codeSx('0.95rem'), color: openable ? 'text.primary' : 'text.disabled' }}
        >
          {nodeName(node, t_)}
        </Typography>
      </Box>

      <Box
        aria-label={t_('Documents') + ': ' + node.documentCount}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.25,
          flexShrink: 0,
          color: openable ? 'text.primary' : 'text.disabled'
        }}
      >
        <DescriptionOutlinedIcon sx={{ fontSize: '0.95rem' }} />
        <Typography variant='caption' sx={{ fontWeight: 700, lineHeight: 1.2 }}>{node.documentCount}</Typography>
      </Box>
    </ButtonBase>
  )
}

const nodeShape = {
  key: PropTypes.string,
  name: PropTypes.string,
  resourceType: PropTypes.string,
  scope: PropTypes.string,
  documentCount: PropTypes.number,
  children: PropTypes.array
}

QrDocumentsTree.propTypes = {
  /** Tree as built by qrDocumentTree.buildDocumentTree. */
  nodes: PropTypes.arrayOf(PropTypes.shape(nodeShape)),
  selectedKey: PropTypes.string,
  onSelect: PropTypes.func.isRequired
}

QrDocumentsBranch.propTypes = {
  node: PropTypes.shape(nodeShape).isRequired,
  selectedKey: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  t_: PropTypes.func.isRequired
}

QrDocumentsRow.propTypes = {
  node: PropTypes.shape(nodeShape).isRequired,
  selected: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
  t_: PropTypes.func.isRequired
}

export default QrDocumentsTree
